import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { t } from '../i18n';
import { Feld, Fehler } from '../components/ui';
import type { Me } from '../App';

export function AccountPage({ me, onChanged }: { me: Me; onChanged: () => Promise<unknown> }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);

  async function changePassword(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setOk(false);
    try {
      await api.post('/api/v1/auth/password/change', { currentPassword: current, newPassword: next });
      setOk(true);
      setCurrent('');
      setNext('');
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'INTERNAL');
    }
  }

  async function startMfa() {
    setMfaError(null);
    const res = await api.post<{ qrDataUrl: string }>('/api/v1/auth/mfa/setup');
    setQr(res.qrDataUrl);
  }

  async function enableMfa(e: FormEvent) {
    e.preventDefault();
    setMfaError(null);
    try {
      await api.post('/api/v1/auth/mfa/enable', { code });
      setQr(null);
      setCode('');
      await onChanged();
    } catch (err) {
      setMfaError(err instanceof ApiError ? err.code : 'INTERNAL');
    }
  }

  return (
    <>
      <h1>{t('account.title')}</h1>
      <p className="meta">
        {me.displayName} · {me.email} · {t(`role.${me.role}`)} · {me.organization.name}
      </p>

      <section className="karte">
        <h2>{t('account.passwordTitle')}</h2>
        <form onSubmit={changePassword}>
          <Feld label={t('account.currentPassword')} required>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
            />
          </Feld>
          <Feld label={t('auth.reset.newPassword')} required hint={t('auth.setup.passwordHint')}>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              minLength={12}
              required
            />
          </Feld>
          {ok && <div className="erfolg">{t('account.passwordChanged')}</div>}
          <Fehler code={error} />
          <div className="aktionen">
            <button type="submit" className="knopf-primaer">
              {t('account.changeAction')}
            </button>
          </div>
        </form>
      </section>

      <section className="karte">
        <h2>{t('account.mfaTitle')}</h2>
        {me.totpEnabled ? (
          <p className="erfolg">{t('account.mfaActive')}</p>
        ) : qr ? (
          <form onSubmit={enableMfa}>
            <p>{t('auth.mfa.setupIntro')}</p>
            <img src={qr} alt={t('auth.mfa.qrAlt')} width={220} height={220} />
            <Feld label={t('auth.mfa.code')} required>
              <input
                inputMode="numeric"
                pattern="[0-9]{6}"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </Feld>
            <Fehler code={mfaError} />
            <button type="submit" className="knopf-primaer">
              {t('account.mfaEnable')}
            </button>
          </form>
        ) : (
          <>
            <p className="erklaerung">{t('account.mfaIntro')}</p>
            <button type="button" className="knopf-sekundaer" onClick={startMfa}>
              {t('account.mfaStart')}
            </button>
          </>
        )}
      </section>
    </>
  );
}
